import Path from '@leverege/path';
import { Plugins } from '@leverege/plugin';
// eslint-disable-next-line import/no-cycle
import PluginSetup from "./PluginSetup.js";
import Start from "./startup/Start.js";
export default class Molten {
  constructor() {
    this.config = null;
    this.props = {};
    this.values = {};
    this.thunkArgs = {};
  }
  static uiVersion = process.env.FIREBASE_HOSTING_VERSION;
  static releaseParent = process.env.FIREBASE_HOSTING_RELEASE_PARENT;

  /**
   * Sets a value to be available to other plugins. This is useful
   * when needing to compute an object( api, history, etc ) that needs
   * to be used in multiple places, such as a thunk, a provider or a
   * reducer. It is recommended that all keys be namespaced to avoid
   * collisions. If the key already exists, an exception will be thrown
   */
  set(key, val) {
    if (this.values[key] !== undefined) {
      throw new Error('Molten: Attempting to set key when the key was already used', key, val);
    }
    this.values[key] = val;
  }

  /**
   * Returns a value set by set()
   */
  get(key) {
    return this.values[key];
  }

  /**
   * Returns an object containing the values
   */
  getValues() {
    return this.values;
  }

  /**
   * This sets a redux thunk argument. Initializors should add values (like api)
   * to the thunk using this call. If the key was already set, an error is throw.
   */
  setThunkArg(key, value) {
    if (this.thunkArgs[key] !== undefined) {
      throw new Error(`Molten: Attempting to set thunk key(${key}) when the key was already used`, value);
    }
    this.thunkArgs[key] = value;
  }

  /**
   * Returns a thunk value set by setThunk()
   */
  getThunkArg(key) {
    return this.thunkArgs[key];
  }

  /**
   * Returns an object containing the thunk args
   */
  getThunkArgs() {
    return this.thunkArgs;
  }

  /**
   * This sets a top level app prop. 
   */
  setProp(key, value) {
    if (this.props[key] !== undefined) {
      throw new Error(`Molten: Attempting to set prop(${key}) when the key was already used`, value);
    }
    this.props[key] = value;
  }

  /**
   * Returns a prop value set by setProp()
   */
  getProp(key) {
    return this.props[key];
  }

  /**
   * Returns an object containing the base apps props
   */
  getProps() {
    return this.props;
  }

  /**
   * Returns the config. This will be a null object until init() is called. This can optionally return
   * a part of the config
   * @param {string} [path] if supplied, this will look into config for you and return the value
   * at that path. The path is '/' separated. If no value is present, dValue is return
   * @param {any} [dValue] the default return value at the path.
   */
  getConfig(path, dValue) {
    if (path == null) {
      return this.config;
    }
    return Path.get(path, this.config, dValue);
  }

  /**
   * This will set the configuration for Molten and load
   * the default Plugins. After this, custom plugins should
   * be loaded
   */
  init(config) {
    this.config = config || {};
    this.excludes = new Set(this.config?.plugins?.excludes);
    const externalMutators = this.config?.plugins?.mutators || [];
    this.mutators = [...externalMutators];

    // load plugins
    if (config?.loadDefaultPlugins !== false) {
      PluginSetup.install(this);
    }
  }
  async create() {
    // Run the startup app
    await Start.run(this);
  }
  render() {
    throw new Error(`Molten subclass ${this.constructor.name} does not implement 'render' method`);
  }
  addPlugin(type, plugin) {
    if (plugin == null) {
      return;
    }
    // console.log( plugin.id, this.excludes, this.excludes.has( plugin.id ) )
    // If plugin is excluded, return
    if (this.excludes && plugin.id && this.excludes.has(plugin.id)) {
      return;
    }

    // Run the mutators to allow removal, replacement or acceptance of a plugin
    let mPlugin = plugin;
    for (let n = 0; n < this.mutators.length; n++) {
      mPlugin = this.mutators[n](type, mPlugin, this);
      if (mPlugin === null) {
        return;
      }
    }
    Plugins.add(type, mPlugin);
  }
  static global() {
    return window.leveregeMolten?.molten;
  }
}